import Grid from '@mui/material/Grid';
import { type TypeParams, type VideoFormState, VideoOption } from '@openx/types/targeting/video.ts';
import { type JSX, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTargetingContext } from '../../utils/context/TargetingContext.tsx';
import { OptionsDropdown } from '../shared/OptionsDropdown/OptionsDropdown.tsx';
import { DrawerTopBox } from '../shared/TargetingDrawer/DrawerTopBox.tsx';
import { MaxAdDuration } from './components/MaxAdDuration/MaxAdDuration.tsx';
import { RewardedVideoDropdown } from './components/RewardedVideo/RewardedVideoDropdown.tsx';
import { TypeOptionDropdown } from './components/TypeOptionDropdown.tsx';
import { VideoSkipabilityDropdown } from './components/VideoSkipability/VideoSkipabilityDropdown.tsx';
import { getTypePlaceholder } from './utils/utils.ts';

type Props = {
  params: VideoFormState;
  onChange: (typeParams: TypeParams) => void;
  selectedType: VideoOption;
  onTypeChange: (type: VideoOption) => void;
};

export const TopBox = ({ params, onChange, selectedType, onTypeChange }: Props): JSX.Element => {
  const { t } = useTranslation();
  const { useVideoOptionsFetch } = useTargetingContext();

  const { options, isLoading } = useVideoOptionsFetch(selectedType);

  const placeholder = useMemo(() => getTypePlaceholder(t, selectedType), [t, selectedType]);

  const labels = useMemo(
    () => ({
      [VideoOption.IN_STREAM_VIDEO_POSITION]: t('In-Stream Position'),
      [VideoOption.PRESENTATION_FORMAT]: t('Presentation Format'),
      [VideoOption.PLACEMENT]: t('Placement'),
      [VideoOption.REWARDED_VIDEO]: t('Rewarded'),
      [VideoOption.VIDEO_SKIPABILITY]: t('Video Skippability'),
    }),
    [t]
  );

  const label = labels[selectedType];

  const dropdown = useMemo(() => {
    switch (selectedType) {
      case VideoOption.REWARDED_VIDEO:
        return (
          <RewardedVideoDropdown
            placeholder={placeholder}
            value={params[selectedType]?.op || null}
            onChange={onChange}
            label={label}
          />
        );
      case VideoOption.VIDEO_SKIPABILITY:
        return (
          <VideoSkipabilityDropdown
            placeholder={placeholder}
            value={params[selectedType]?.val || null}
            onChange={onChange}
            label={label}
          />
        );
      case VideoOption.MAX_AD_DURATION:
        return <MaxAdDuration value={params[selectedType] || null} onChange={onChange} />;
      default:
        return (
          <OptionsDropdown
            loading={isLoading}
            options={options}
            onChange={val => onChange({ ...params[selectedType], val })}
            selectedOptions={params[selectedType].val as Set<string>}
            placeholder={placeholder}
            dataTest="video-input"
            label={label}
          />
        );
    }
  }, [selectedType, placeholder, params, isLoading, options, onChange, label]);

  return (
    <DrawerTopBox>
      <Grid
        container
        spacing={1}
        sx={{
          alignItems: 'flex-end',
          flexGrow: 1,
        }}
      >
        <Grid size={4} sx={{ height: '100%' }}>
          <TypeOptionDropdown onChange={onTypeChange} value={selectedType} />
        </Grid>
        <Grid size={8}>{dropdown}</Grid>
      </Grid>
    </DrawerTopBox>
  );
};
